"use client";
import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./icons";
import { useWorkspace } from "./workspace";
const buttonVariants = {
  primary:
    "bg-teal text-white border-teal shadow-[0_1px_2px_#0e7c8610] [&:hover:not(:disabled)]:bg-[#096b74]",
  secondary:
    "bg-white text-ink [&:hover:not(:disabled)]:bg-mist [&:hover:not(:disabled)]:border-[#cbd4e0]",
  ghost:
    "bg-transparent border-transparent text-muted [&:hover:not(:disabled)]:bg-line-soft",
  danger: "bg-danger text-white border-danger",
};
const tagVariants: Record<string, string> = {
  teal: "bg-teal-soft text-[#086b74]",
  success: "bg-success-soft text-[#156f38]",
  danger: "bg-danger-soft text-[#b52c37]",
  warning: "bg-amber-soft text-[#94500e]",
  muted: "bg-[#eff2f6] text-[#627087]",
};
export function Button({
  children,
  onClick,
  variant = "secondary",
  icon,
  disabled,
  type = "button",
  className = "",
}: {
  children?: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  icon?: string;
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}) {
  return (
    <button
      type={type}
      className={`btn inline-flex items-center justify-center gap-1.75 border border-line rounded-[7px] min-h-8.5 py-1.75 px-3 text-[11px] font-[550] whitespace-nowrap transition-colors duration-150 [&_.icon]:w-3.75 [&_.icon]:h-3.75 [&.assistant-button]:bg-teal-soft [&.assistant-button]:border-[#cbe5e8] [&.assistant-button]:text-teal max-[760px]:[&.assistant-button]:hidden ${buttonVariants[variant]}  ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && <Icon name={icon} />} {children}
    </button>
  );
}
export function Tag({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: string;
}) {
  return (
    <span
      className={`tag text-[9px] font-[550] leading-[1.5] py-0.75 px-1.75 rounded-[5px] inline-flex items-center gap-1 whitespace-nowrap [&_.icon]:w-2.75 [&_.icon]:h-2.75 ${tagVariants[tone] || tagVariants.muted}`}
    >
      {children}
    </span>
  );
}
const cardVariants = {
  default: "bg-white border-line",
  ai: "bg-[#f0f8f9] border-[#cee6e9] [&_.card-heading_h2]:text-teal",
  automation:
    "bg-[#f1f8f9] border-[#d4e9ec] [&_.card-heading_h2]:text-teal [&>p]:text-[#516c78] [&>p]:text-[11px] [&>p]:mt-2.75 [&>p]:mb-4.75 [&>p]:leading-[1.8] [&>.text-link]:text-[10px]",
};
export function Card({
  tone = "default",
  title,
  children,
  className = "",
  action,
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
  action?: ReactNode;
  tone?: keyof typeof cardVariants;
}) {
  return (
    <section
      className={`card border rounded-card shadow-card p-5 min-w-0 max-[1200px]:p-4.25 ${cardVariants[tone]} ${className}`}
    >
      {(title || action) && (
        <div className="card-heading flex justify-between items-center gap-2.5 mb-4.5 [&_h2]:text-[12px] [&_h2]:leading-[1.7] [&_h2]:flex [&_h2]:items-center [&_h2]:gap-2 [&_.icon]:w-4 [&_.icon]:h-4 min-[1600px]:[&_h2]:text-[13px] max-[760px]:[&_h2]:text-[12px]">
          <h2>{title}</h2>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header flex justify-between items-center gap-5 mb-6.25 [&_.eyebrow]:mb-1.25 max-[1200px]:items-start max-[1200px]:[&_h1]:text-[23px] max-[1000px]:flex-wrap max-[760px]:gap-3 max-[760px]:mb-5 max-[760px]:[&_h1]:text-[22px] max-[450px]:[&_h1_.tag]:text-[8px] max-[450px]:[&_h1_.tag]:ml-0.75">
      <div>
        {eyebrow && (
          <p className="eyebrow text-[10px] tracking-[1.3px] uppercase text-teal font-semibold">
            {eyebrow}
          </p>
        )}
        <h1 className="[&_.tag]:align-middle [&_.tag]:tracking-normal [&_.tag]:ml-1.75">
          {title}
        </h1>
        {description && (
          <div className="page-description text-[11.5px] text-muted mt-1.5 leading-[1.8] [&_b]:text-ink max-[760px]:text-[11px]">
            {description}
          </div>
        )}
      </div>
      {actions && (
        <div className="page-actions flex items-center gap-2 flex-none max-[1200px]:[&_.compact-select]:max-w-[120px] max-[1000px]:mt-0.75">
          {actions}
        </div>
      )}
    </header>
  );
}
export function Stats({
  items,
}: {
  items: {
    label: string;
    value: ReactNode;
    hint?: ReactNode;
    icon?: string;
    tone?: string;
  }[];
}) {
  return (
    <div className="stats-grid grid grid-cols-4 gap-4 mb-6.25 max-[1200px]:gap-3 max-[1000px]:grid-cols-2 max-[760px]:gap-2.75 max-[760px]:mb-5">
      {items.map((item) => (
        <div
          className={`stat card border rounded-card shadow-card min-w-0 py-4.5 px-4.75 min-[1600px]:p-5.25 max-[1200px]:p-3.75 ${item.tone === "teal" ? "border-[#cce4e7] bg-linear-[115deg,#fff,#f2fafb] [&_.stat-value]:text-teal [&_.stat-icon]:text-teal [&_.stat-icon]:bg-teal-soft" : item.tone === "warning" ? "bg-white border-line [&_.stat-value]:text-amber [&_.stat-icon]:text-amber [&_.stat-icon]:bg-amber-soft" : "bg-white border-line"}`}
          key={item.label}
        >
          <div className="stat-top flex items-center justify-between gap-2 text-muted text-[10.5px] min-[1600px]:text-[11px] max-[1200px]:text-[9.5px] max-[760px]:text-[10px] max-[450px]:text-[9px]">
            <span>{item.label}</span>
            {item.icon && (
              <span className="stat-icon text-[#8693a5] flex items-center justify-center w-7.25 h-7.25 rounded-lg bg-mist flex-none max-[450px]:w-6 max-[450px]:h-6 max-[450px]:[&_>_.icon]:w-3.5 max-[450px]:[&_>_.icon]:h-3.5">
                <Icon name={item.icon} />
              </span>
            )}
          </div>
          <div className="stat-value font-display text-[28px] font-semibold tracking-[-0.7px] leading-[1.35] mt-2 mx-0 mb-1.25 tabular-nums max-[1200px]:text-[25px] max-[760px]:text-[25px] max-[450px]:text-[24px]">
            {item.value}
          </div>
          {item.hint && (
            <div className="stat-hint text-[9.5px] text-muted leading-[1.7] max-[1200px]:text-[9px] max-[760px]:text-[9px] max-[450px]:text-[8px]">
              {item.hint}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
export function Tabs({
  items,
  value,
  onChange,
}: {
  items: { id: string; label: string; count?: number }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div
      className="tabs flex gap-4.75 overflow-x-auto border-b border-b-line mt-0 mx-0 mb-5 [scrollbar-width:thin] [&_button]:whitespace-nowrap [&_button]:flex [&_button]:items-center [&_button]:gap-1.75 [&_button]:bg-transparent [&_button]:border-0 [&_button]:border-b-2 [&_button]:border-b-transparent [&_button]:py-3 [&_button]:px-0 [&_button]:text-[11px] [&_button]:text-muted [&_button]:-mb-0.25 [&_button.active]:text-teal [&_button.active]:border-b-teal [&_button.active]:font-semibold [&_button_>_span]:text-[9px] [&_button_>_span]:bg-teal-soft [&_button_>_span]:rounded-[5px] [&_button_>_span]:py-0.25 [&_button_>_span]:px-1.25 max-[760px]:gap-4.5 max-[760px]:[&_button]:text-[10px]"
      role="tablist"
    >
      {items.map((item) => (
        <button
          type="button"
          role="tab"
          aria-selected={value === item.id}
          key={item.id}
          onClick={() => onChange(item.id)}
          className={value === item.id ? "active" : ""}
        >
          {item.label}
          {item.count !== undefined && <span>{item.count}</span>}
        </button>
      ))}
    </div>
  );
}
export function Empty({
  title,
  description,
}: {
  title?: string;
  description?: string;
}) {
  const { t } = useWorkspace();
  return (
    <div className="empty-state py-11.25 px-3.75 text-center flex items-center flex-col gap-2.5 [&_h3]:text-[14px] [&_p]:text-muted [&_p]:text-[12px] [&_p]:max-w-[300px]">
      <div className="empty-icon w-11 h-11 flex items-center justify-center bg-teal-soft text-teal rounded-full mb-1">
        <Icon name="check" />
      </div>
      <h3>{title || t("empty")}</h3>
      {description && <p>{description}</p>}
    </div>
  );
}
export function Progress({
  value,
  label,
  detail,
}: {
  value: number;
  label?: string;
  detail?: string;
}) {
  return (
    <div className="progress-block my-3.75 mx-0 [&_>_.between]:text-[10px] [&_>_.between]:mb-1.75 [&_>_.between]:text-muted [&_b]:text-[10px] [&_b]:font-medium [&_b]:text-ink">
      {label && (
        <div className="between flex justify-between items-center gap-3">
          <span>{label}</span>
          <b>{detail || `${value}%`}</b>
        </div>
      )}
      <div
        className="progress-track h-1.5 bg-[#e9eff3] overflow-hidden rounded-[9px] [&_>_span]:block [&_>_span]:h-full [&_>_span]:bg-teal [&_>_span]:rounded-[9px]"
        role="progressbar"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span style={{ width: `${Math.min(100, value)}%` }} />
      </div>
    </div>
  );
}
export function Modal({
  title,
  description,
  children,
  onClose,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useWorkspace();
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="modal fixed inset-0 m-auto overflow-y-auto border border-line rounded-[17px] p-6 w-[min(570px,_calc(100vw_-_30px))] max-h-[88vh] text-ink shadow-[0_25px_100px_#0f1b2d40] bg-white [&::backdrop]:bg-[#0b162c65] [&::backdrop]:backdrop-blur-[3px] max-[450px]:p-5"
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      aria-labelledby="modal-title"
    >
      <div className="modal-heading flex justify-between items-start gap-3.75 mb-5.5 [&_h2]:text-[17px] [&_h2]:leading-[1.6] [&_p]:text-[11px] [&_p]:text-muted [&_p]:mt-1.5 [&_p]:leading-[1.8] [&_>_.icon-button]:-mt-0.75">
        <div>
          <h2 id="modal-title">{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <button
          type="button"
          className="icon-button border border-transparent bg-transparent inline-flex items-center justify-center w-8.5 h-8.5 rounded-lg text-muted flex-none relative [&:hover]:bg-line-soft [&:hover]:text-ink [&.mobile-toggle]:hidden max-[760px]:[&.mobile-toggle]:inline-flex"
          onClick={onClose}
          aria-label={t("close")}
        >
          <Icon name="close" />
        </button>
      </div>
      {children}
    </dialog>
  );
}
export function DataTable({
  headers,
  children,
}: {
  headers: string[];
  children: ReactNode;
}) {
  return (
    <div className="[&_table]:w-full [&_table]:border-collapse [&_table]:whitespace-nowrap [&_table]:text-left [&_th]:text-[9px] [&_th]:tracking-[0.2px] [&_th]:font-[550] [&_th]:text-muted [&_th]:py-2.75 [&_th]:px-2.5 [&_th]:bg-[#f8f9fc] [&_th]:border-t [&_th]:border-t-line-soft [&_th]:border-b [&_th]:border-b-line [&_th]:text-left [&_td]:py-3.5 [&_td]:px-2.5 [&_td]:text-[10.5px] [&_td]:border-b [&_td]:border-b-line-soft [&_td]:align-middle [&_th:first-child]:pl-0 [&_td:first-child]:pl-0 [&_th:last-child]:pr-0 [&_td:last-child]:pr-0 [&_tbody_tr:last-child_td]:border-b-0 [&_tr:hover_td]:bg-[#f9fbfc] table-scroll overflow-x-auto w-full">
      <table>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={`${h}-${i}`} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
export function Sparkline({ label }: { label: string }) {
  return (
    <svg
      className="sparkline w-full h-[160px] block mt-5 mx-0 mb-1.5"
      viewBox="0 0 600 160"
      role="img"
      aria-label={label}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="balance-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0e7c86" stopOpacity=".18" />
          <stop offset="100%" stopColor="#0e7c86" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[25, 65, 105, 145].map((y) => (
        <path
          key={y}
          d={`M0 ${y}H600`}
          stroke="#e6ebf2"
          strokeDasharray="4 5"
        />
      ))}
      <path
        d="M0 128L55 116L110 124L165 92L220 101L275 68L330 81L385 48L440 60L495 27L550 35L600 16V160H0Z"
        fill="url(#balance-fill)"
      />
      <path
        d="M0 128L55 116L110 124L165 92L220 101L275 68L330 81L385 48L440 60L495 27L550 35L600 16"
        fill="none"
        stroke="#0e7c86"
        strokeWidth="3"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
export function downloadText(
  filename: string,
  text: string,
  type = "text/plain;charset=utf-8",
) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function Field({ children }: { children: ReactNode }) {
  return (
    <label className="my-4 flex flex-col gap-1.75 text-[11px] font-medium [&_:is(input:not([type=file]),textarea,select)]:w-full [&_:is(input:not([type=file]),textarea,select)]:rounded-[7px] [&_:is(input:not([type=file]),textarea,select)]:border [&_:is(input:not([type=file]),textarea,select)]:border-[#d7e0eb] [&_:is(input:not([type=file]),textarea,select)]:bg-white [&_:is(input:not([type=file]),textarea,select)]:px-3 [&_:is(input:not([type=file]),textarea,select)]:py-2.5 [&_:is(input:not([type=file]),textarea,select)]:text-xs [&_:is(input:not([type=file]),textarea,select)]:font-normal [&_:is(input:not([type=file]),textarea,select)]:leading-[1.7] [&_textarea]:min-h-20 [&_textarea]:resize-y [&_input[type=file]]:max-w-full [&_input[type=file]]:text-[10px]">
      {children}
    </label>
  );
}
